import React, { useState } from 'react';
import { Box, Grid, Typography, CircularProgress, MenuItem } from '@mui/material';
import { TextField, CustomCheckbox, CustomButton, CustomSelect } from '../../../../../v2/MUI_custom_component';
import FileUploadWrapper from '../../../../../v2/Utils/FileUpload';
import CustomFileUploadInput from './CustomFileUploadInput';
import { getAge, calculateAgesAndFlags } from './../../../../../v2/Utils/AgeCalc';

const EnhancedFileUploadArea = ({ 
    onFileUpload, 
    onFileDelete,
    isLoading, 
    error, 
    uploadedFile, 
    extractedData, 
    phoneNumber, 
    onPhoneChange, 
    relation,
    onRelationChange,
    relations = [],
    onConfirm, 
    uploadTitle = "Upload Beneficiary Aadhar",
    processingText = "Processing Aadhar..."
}) => {
    const [confirm, setConfirm] = useState(false);
    const [phoneValidation, setPhoneValidation] = useState('');
    const [relationValidation, setRelationValidation] = useState('');
    const [consentValidation, setConsentValidation] = useState('');
    const [ageValidation, setAgeValidation] = useState('');

    const handleUpload = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            onFileUpload(files[0]);
        }
    };

    const handlePhoneChange = (value) => {
        onPhoneChange(value);
        if (!value.trim()) {
            setPhoneValidation('Phone number is required');
        } else if (!/^\d{10}$/.test(value.replace(/\D/g, ''))) {
            setPhoneValidation('Please enter a valid 10-digit phone number');
        } else {
            setPhoneValidation('');
        }
    };

    const handleConfirmClick = () => {
        // Reset all validations
        setPhoneValidation('');
        setRelationValidation('');
        setConsentValidation('');
        setAgeValidation('');

        if (!phoneNumber.trim()) {
            setPhoneValidation('Phone number is required');
            return;
        }
        if (!/^\d{10}$/.test(phoneNumber.replace(/\D/g, ''))) {
            setPhoneValidation('Please enter a valid 10-digit phone number');
            return;
        }

        if (uploadTitle.includes('Beneficiary') && extractedData.dob) {
            const ageResult = calculateAgesAndFlags([extractedData.dob]);
            if (ageResult[0]?.ageFlag) {
                const age = ageResult[0]?.age;
                if (age < 18) {
                    setAgeValidation('Age should be 18+');
                } else if (age > 60) {
                    setAgeValidation('Age must be under 60 years');
                }
                return;
            }
        }

        if (uploadTitle.includes('Nominee') && !relation) {
            setRelationValidation('Please select a relation');
            return;
        }
        if (!confirm) {
            setConsentValidation('Please confirm the details to proceed');
            return;
        }
        onConfirm();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
             <Grid container spacing={2} sx={{ my: 0}}>
                {uploadTitle.includes('Nominee') && onRelationChange && (
                    <Grid item xs={12}>
                        <CustomSelect
                            id="relation-select"
                            label="Select Relation*"
                            value={relation || ''}
                            onChange={(e) => onRelationChange(e.target.value)}
                            fullWidth
                            error={!!relationValidation}
                            helperText={relationValidation}
                        >
                            {relations && relations.length > 0 ? (
                                relations.map((relationOption) => (
                                    <MenuItem key={relationOption.value} value={relationOption.value}>
                                        {relationOption.label}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="" disabled>
                                    No relations available
                                </MenuItem>
                            )}
                        </CustomSelect>
                    </Grid>
                )}
            </Grid>
            {isLoading ? (
                <Box sx={{ 
                    border: '2px dashed #ddd',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#fafafa'
                }}>
                    <CircularProgress size={48} sx={{ mb: 2 }} />
                    <Typography variant="body1" color="primary">
                        {processingText}
                    </Typography>
                </Box>
            ) : !uploadedFile ? (
                <Box>
                    <CustomFileUploadInput
                        title={uploadTitle}
                        acceptedFileTypes={['.jpg', '.jpeg', '.png', '.pdf']}
                        onFileSelect={onFileUpload}
                    />
                    {error && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box>
                    <FileUploadWrapper
                        handleUpload={handleUpload}
                        title={uploadTitle}
                        acceptedFileTypes={['.jpg', '.jpeg', '.png', '.pdf']}
                        existingFileName={uploadedFile.name}
                        existingFileUrl={URL.createObjectURL(uploadedFile)}
                        showOriginalOnDelete={false}
                        onDelete={() => {
                            if (onFileDelete) {
                                onFileDelete();
                            }
                        }}
                    />
                    {extractedData && Object.keys(extractedData).length > 0 && (
                        <Box sx={{ p: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Name
                                    </Typography>
                                    <Typography variant="body1">
                                        {extractedData.full_name}
                                    </Typography>
                                </Grid>
                                
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Date of Birth
                                    </Typography>
                                    <Typography variant="body1">
                                        {new Date(extractedData.dob).toLocaleDateString('en-GB', { 
                                            day: 'numeric', 
                                            month: 'long', 
                                            year: 'numeric' 
                                        })}
                                    </Typography>
                                </Grid>
                                {extractedData.dob && (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="labelD" sx={{ color: '#666' }}>
                                            Age
                                        </Typography>
                                        <Typography 
                                            variant="body1"
                                            sx={{
                                                color: uploadTitle.includes('Beneficiary') && 
                                                       calculateAgesAndFlags([extractedData.dob])[0]?.ageFlag 
                                                       ? 'error.main' : 'inherit'
                                            }}
                                        >
                                            {getAge(extractedData.dob)} years
                                            {ageValidation && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                    {ageValidation}
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Grid>
                                )}
                                
                               
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Gender
                                    </Typography>
                                    <Typography variant="body1">
                                        {extractedData.gender}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>
            )}

            {/* Phone Number Field */}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Phone number*"
                        placeholder="Enter Phone Number"
                        value={phoneNumber}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        error={!!phoneValidation}
                        helperText={phoneValidation}
                        inputProps={{ maxLength: 10 }}
                        fullWidth
                        variant="outlined"
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
                <CustomCheckbox 
                    label="Details provided herein are true and I have obtained all relevant organisational consents for the purposes."
                    checked={confirm}
                    onChange={(e) => setConfirm(e.target.checked)}
                />
                {consentValidation && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {consentValidation}
                    </Typography>
                )}
                
            </Box>

            {/* Action Button */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <CustomButton
                    variant="filled"
                    onClick={extractedData && Object.keys(extractedData).length > 0 ? handleConfirmClick : () => {}}
                    disabled={isLoading || (!extractedData || Object.keys(extractedData).length === 0)}
                    label="Save & Continue"
                    fullWidth
                />
            </Box>
        </Box>
    );
};

export default EnhancedFileUploadArea;